import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FileSignature, Loader2, Plus, Printer, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Empty, ErrorBox, Field, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";
import { RxSheet } from "@/components/RxSheet";

const PF = [["display_name", "Full name with title (e.g. Dr. Juan Dela Cruz, MD)"], ["specialty", "Specialty"], ["prc_no", "PRC license no."], ["ptr_no", "PTR no."],
  ["s2_no", "S2 no. (optional)"], ["clinic_name", "Clinic name"], ["clinic_address", "Clinic address"], ["clinic_contact", "Clinic contact"]];

export function DoctorProfile() {
  const { data, setData } = useFetch("/rx/provider/profile");
  const [busy, setBusy] = useState(false);
  if (!data) return <Spinner />;
  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/image\/(png|jpeg)/.test(f.type) || f.size > 400_000) { toast.error("Use a PNG or JPG under 400 KB"); return; }
    const r = new FileReader();
    r.onload = () => setData({ ...data, signature: r.result });
    r.readAsDataURL(f);
  };
  const save = async () => {
    setBusy(true);
    try { setData((await api.put("/rx/provider/profile", data)).data); toast.success("Doctor profile saved"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-2xl space-y-4">
      <PageHead eyebrow="Doctor Portal" title="Prescription profile" sub="Shown on your e-RX and on coupons you sign. PRC number and signature are required to issue." />
      <div className="surface grid gap-3 p-5 sm:grid-cols-2">
        {PF.map(([k, l]) => <Field key={k} label={l}><input className="field" value={data[k] || ""} onChange={set(k)} data-testid={`profile-${k}`} /></Field>)}
      </div>
      <div className="surface flex flex-wrap items-center gap-4 p-5">
        {data.signature ? <img src={data.signature} alt="Signature" className="h-16 rounded-lg bg-white object-contain ring-1 ring-slate-200" data-testid="signature-preview" /> : <p className="text-sm text-slate-500">No signature uploaded yet.</p>}
        <label className="btn-secondary btn-sm cursor-pointer"><Upload size={13} /> Upload signature<input type="file" accept="image/png,image/jpeg" className="hidden" onChange={pick} data-testid="signature-upload" /></label>
      </div>
      <button className="btn-primary w-full py-3" disabled={busy} onClick={save} data-testid="save-profile-button">Save profile</button>
    </div>
  );
}

const BLANK_MED = { generic: "", brand: "", strength: "", quantity: "", sig: "" };

export function RxNew() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const prof = useFetch("/rx/provider/profile");
  const [serial, setSerial] = useState("");
  const [base, setBase] = useState(null);
  const [items, setItems] = useState([{ ...BLANK_MED }]);
  const [extra, setExtra] = useState({ notes: "", follow_up: "" });
  const [preview, setPreview] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async (q) => { setError(""); try { setBase((await api.get(`/rx/provider/prefill?${q}`)).data); } catch (e) { setError(errMsg(e)); } };
  useEffect(() => { if (params.get("sr")) load(`sr=${params.get("sr")}`); else if (params.get("tc")) load(`tc=${params.get("tc")}`); }, [params]);
  const setP = (k) => (e) => setBase({ ...base, patient: { ...base.patient, [k]: k === "age" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value } });
  const setM = (i, k) => (e) => setItems(items.map((m, j) => (j === i ? { ...m, [k]: e.target.value } : m)));
  const issue = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/rx/provider/prescriptions", { ...base, items, notes: extra.notes, follow_up: extra.follow_up || null });
      toast.success(`${data.number} issued`); nav(`/print/rx/${data.number}`);
    } catch (e) { setError(errMsg(e)); setPreview(false); } finally { setBusy(false); }
  };
  if (!prof.data) return <Spinner />;
  const ready = prof.data.prc_no && prof.data.signature;
  const ok = base && base.patient.name && items.every((m) => m.generic && m.quantity && m.sig);
  return (
    <div className="max-w-3xl space-y-4">
      <PageHead eyebrow="Doctor Portal" title="Write e-RX" />
      {!ready && <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900" data-testid="rx-profile-missing">Add your PRC license number and signature first. <Link to="/provider/profile" className="font-bold underline">Open profile</Link></p>}
      {!base ? (
        <form className="surface flex gap-2 p-4" onSubmit={(e) => { e.preventDefault(); load(`serial=${encodeURIComponent(serial)}`); }}>
          <input className="field font-mono uppercase" placeholder="Member's card serial" value={serial} onChange={(e) => setSerial(e.target.value)} required data-testid="rx-serial-input" />
          <button className="btn-primary" data-testid="rx-find-member">Find member</button>
        </form>
      ) : preview ? (
        <div className="space-y-3">
          <RxSheet rx={{ ...base, items, ...extra, doctor: prof.data, signature: prof.data.signature }} />
          <div className="flex gap-2"><button className="btn-secondary flex-1" onClick={() => setPreview(false)} data-testid="rx-edit-button">Edit</button>
            <button className="btn-primary flex-1 py-3" disabled={busy || !ready} onClick={issue} data-testid="rx-sign-issue-button">{busy ? <Loader2 size={15} className="animate-spin" /> : <FileSignature size={15} />} Sign & issue</button></div>
        </div>
      ) : (
        <div className="surface space-y-4 p-5">
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="sm:col-span-2"><Field label="Patient name"><input className="field" value={base.patient.name} onChange={setP("name")} data-testid="rx-patient-name" /></Field></div>
            <Field label="Age"><input type="number" className="field" value={base.patient.age ?? ""} onChange={setP("age")} data-testid="rx-patient-age" /></Field>
            <Field label="Sex"><select className="field" value={base.patient.sex} onChange={setP("sex")} data-testid="rx-patient-sex"><option value="">—</option><option>M</option><option>F</option></select></Field>
            <div className="sm:col-span-4"><Field label="Address"><input className="field" value={base.patient.address} onChange={setP("address")} data-testid="rx-patient-address" /></Field></div>
          </div>
          {items.map((m, i) => (
            <div key={i} className="grid gap-2 rounded-xl bg-slate-50 p-3 sm:grid-cols-6" data-testid={`rx-med-${i}`}>
              <input className="field sm:col-span-2" placeholder="Generic name" value={m.generic} onChange={setM(i, "generic")} data-testid={`rx-med-generic-${i}`} />
              <input className="field" placeholder="Brand" value={m.brand} onChange={setM(i, "brand")} />
              <input className="field" placeholder="Strength/form" value={m.strength} onChange={setM(i, "strength")} data-testid={`rx-med-strength-${i}`} />
              <input className="field" placeholder="Qty" value={m.quantity} onChange={setM(i, "quantity")} data-testid={`rx-med-qty-${i}`} />
              <button type="button" className="btn-ghost btn-sm" disabled={items.length === 1} onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash2 size={13} /></button>
              <input className="field sm:col-span-6" placeholder="Sig / directions" value={m.sig} onChange={setM(i, "sig")} data-testid={`rx-med-sig-${i}`} />
            </div>
          ))}
          <button type="button" className="btn-secondary btn-sm" onClick={() => setItems([...items, { ...BLANK_MED }])} data-testid="rx-add-med"><Plus size={13} /> Add medicine</button>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="sm:col-span-2"><Field label="Notes"><input className="field" value={extra.notes} onChange={(e) => setExtra({ ...extra, notes: e.target.value })} data-testid="rx-notes" /></Field></div>
            <Field label="Follow-up"><input type="date" className="field" value={extra.follow_up} onChange={(e) => setExtra({ ...extra, follow_up: e.target.value })} data-testid="rx-follow-up" /></Field>
          </div>
          <button className="btn-primary w-full py-3" disabled={!ok} onClick={() => setPreview(true)} data-testid="rx-preview-button">Review prescription</button>
        </div>
      )}
      <ErrorBox error={error} />
    </div>
  );
}

export function RxList() {
  const { data, reload } = useFetch("/rx/provider/prescriptions");
  const voidRx = async (no) => {
    const reason = window.prompt("Reason for voiding this prescription?");
    if (!reason) return;
    try { await api.post(`/rx/provider/prescriptions/${no}/void`, { reason }); toast.success("Prescription voided"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!data) return <Spinner />;
  return (
    <div className="space-y-4">
      <PageHead eyebrow="Doctor Portal" title="e-RX" actions={<Link to="/provider/rx/new" className="btn-primary" data-testid="new-rx-button"><Plus size={15} /> Write e-RX</Link>} />
      {!data.length ? <Empty title="No prescriptions yet" /> : (
        <div className="surface overflow-x-auto"><table className="data-table" data-testid="rx-table">
          <thead><tr><th>No.</th><th>Patient</th><th>Medicines</th><th>Status</th><th>Issued</th><th /></tr></thead>
          <tbody>{data.map((r) => (
            <tr key={r.number}><td className="font-mono text-xs">{r.number}</td><td>{r.patient.name}</td><td className="text-xs">{r.items.map((m) => m.generic).join(", ")}</td>
              <td><StatusBadge status={r.status} /></td><td className="text-xs">{fmtDateTime(r.issued_at)}</td>
              <td className="flex gap-1"><Link to={`/print/rx/${r.number}`} className="btn-ghost btn-sm" data-testid={`rx-print-${r.number}`}><Printer size={13} /></Link>
                {r.status === "VALID" && <button className="btn-ghost btn-sm text-red-600" onClick={() => voidRx(r.number)} data-testid={`rx-void-${r.number}`}>Void</button>}</td></tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
}
